<template>
  <div>
    <b-card
      v-for="user in assignedInstructors"
      :key="user.id"
      no-body
      class="border d-flex w-100 mb-1"
    >
      <b-card-header class="d-flex flex-row justify-content-between align-items-start pt-100 pb-75 border-bottom">
        <div>
          <h5 class="mb-0">
            {{ user.name }}
          </h5>
          <b-badge variant="light-info text-capitalize" style="margin-top: 0.2rem"> Instructor </b-badge>
        </div>

        <div>
          <b-button class="" v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" block @click="unassignInstructor(user.id)">
            Unassign
          </b-button>
        </div>
      </b-card-header>

      <b-card-body class="d-flex flex-column justify-content-center pb-0">
        <table class="w-100">
          <tr>
            <th class="pb-50">
              <span>Phone #</span>
            </th>

            <td class="pb-50">
              {{ user.phoneNumber }}
            </td>
          </tr>
          <tr>
            <th>
              <span>Email</span>
            </th>
            <td>
              {{ user.email }}
            </td>
          </tr>
        </table>
      </b-card-body>
    </b-card>

    <b-card no-body class="mb-0 w-100">
      <b-card-header>
        <h5 class="mb-0" v-if="!assignedInstructors.length">No Instructors Assigned</h5>
        <h5 class="mb-0" v-else>Add Another Instructor</h5>
      </b-card-header>
      <b-card-body class="d-flex flex-column justify-content-between">
        <vue-autosuggest
          ref="autocomplete"
          v-model="instructorQuery"
          :suggestions="instructorSuggestions"
          :input-props="instructorInputProps"
          :section-configs="instructorSectionConfigs"
          :render-suggestion="instructorRenderSuggestion"
          @input="instructorFetchResults"
        />

        <b-button @click="doSelectInstructor" variant="primary" class="mt-1" block>Assign Instructor</b-button>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BBadge, BButton, BAvatar, BRow, BLink, BMedia } from 'bootstrap-vue';
import Ripple from 'vue-ripple-directive';
import { onUnmounted, ref, computed } from '@vue/composition-api';
import store from '@/store';
import userStoreModule from '@/store/user/userStoreModule';
import router from '@/router';
import useCourseSections from '../useCourseSections';
import { avatarText } from '@core/utils/filter';
import rosterStoreModule from '@/store/roster/rosterStoreModule';
import { VueAutosuggest } from 'vue-autosuggest';

export default {
  directives: {
    Ripple,
  },
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
    BButton,
    BAvatar,
    BRow,
    BLink,
    BMedia,
    VueAutosuggest,
  },
  props: {
    instructors: {
      type: Array,
      required: false,
      default: () => [],
    },
    courseSectionData: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.instructorFetchResults();
  },
  computed: {
    assignedInstructors() {
      return (this.$props.instructors || []).filter((u) => u && u.id);
    },
  },
  methods: {
    instructorFetchResults() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(async () => {
        const usersPromise = this.$store.dispatch('userStoreModule/fetchUsers', {
          search: this.instructorQuery,
        });

        Promise.all([usersPromise]).then((values) => {
          this.instructorSuggestions = [];

          const users = values[0].data.results;

          users.length && this.instructorSuggestions.push({ name: 'users', data: users });
        });
      }, 250);
    },
    instructorRenderSuggestion(suggestion) {
      const user = suggestion.item;
      return (
        <div class="d-flex">
          <div>
            <b-avatar src={user.avatar} text={avatarText(user.name)} class="mr-50" variant="light-info" size="30" />
          </div>
          <div class="d-flex align-items-center">
            <h4 class="font-weight-normal mb-0">{user.name}</h4>
          </div>
        </div>
      );
    },
    async doSelectInstructor() {
      if (!this.instructorSelected) {
        return;
      }

      const existingIds = this.assignedInstructors.map((u) => u.id);
      if (existingIds.includes(this.instructorSelected.id)) {
        this.instructorQuery = '';
        this.instructorSelected = null;
        return;
      }

      this.$store
        .dispatch('rosterStoreModule/updateCourseSection', {
          courseSectionId: this.courseSectionData.id,
          courseSectionData: {
            instructors: [...existingIds, this.instructorSelected.id],
          },
        })
        .then(() => {
          this.instructorQuery = '';
          this.instructorSelected = null;
          this.$emit('refetch-data');
        })
        .catch((e) => {
          console.error('failed to update course', e);
        });
    },
    unassignInstructor(id) {
      const remaining = this.assignedInstructors.map((u) => u.id).filter((uid) => uid !== id);
      this.$store
        .dispatch('rosterStoreModule/updateCourseSection', {
          courseSectionId: this.$route.params.id,
          courseSectionData: { instructors: remaining },
        })
        .then(() => {
          this.$emit('refetch-data');
        })
        .catch((e) => {
          console.error('failed to update course', e);
        });
    },
  },
  setup() {
    const { resolveUserRoleVariant } = useCourseSections();

    const instructorQuery = ref('');
    const instructorSuggestions = ref([]);
    const instructorSelected = ref(undefined);
    const instructorSectionConfigs = {
      users: {
        limit: 5,
        onSelected: (selected) => {
          instructorSelected.value = selected.item;
        },
      },
    };

    const instructorInputProps = {
      id: 'autosuggest__input_ajax',
      placeholder: 'Search for users',
      class: 'form-control',
      name: 'ajax',
    };

    return {
      avatarText,
      resolveUserRoleVariant,

      instructorQuery,
      instructorSuggestions,
      instructorSectionConfigs,
      instructorInputProps,
      instructorSelected,
    };
  },
};
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';
</style>
